import { useEffect, useState } from "react";
import { Button, Input, Stack } from "@salesapp/ui";
import { crmApi, type Contact } from "../../lib/crmApi";

export type ContactChoice = {
  name: string;
  contactId: string;
  companyId: string;
  companyName: string;
  email: string;
};

function contactEmail(person: Contact): string {
  return person.work_email || person.email || person.personal_email || "";
}

function contactName(person: Contact): string {
  return `${person.first_name} ${person.last_name ?? ""}`.trim();
}

export function ContactLookup({
  orgId,
  companyId,
  value,
  contactId,
  onChange,
}: {
  orgId: string;
  companyId?: string;
  value: string;
  contactId: string;
  onChange: (next: ContactChoice) => void;
}) {
  const [matches, setMatches] = useState<Contact[]>([]);

  useEffect(() => {
    if (!value.trim() || contactId) {
      setMatches([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .listContacts(orgId, companyId || undefined, value.trim())
        .then(setMatches)
        .catch(() => setMatches([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgId, companyId, value, contactId]);

  return (
    <Stack gap={2}>
      <Input
        label="Contact"
        value={value}
        onChange={(event) =>
          onChange({
            name: event.target.value,
            contactId: "",
            companyId: companyId ?? "",
            companyName: "",
            email: "",
          })
        }
        placeholder="Search by name, email, or phone"
      />
      {contactId ? (
        <p className="crm-help">Linked to an existing contact. Company details fill from that person.</p>
      ) : value.trim() ? (
        <p className="crm-help">Pick a contact from the matching results below.</p>
      ) : null}
      {matches.length > 0 && !contactId ? (
        <ul className="crm-lookup-list">
          {matches.map((person) => {
            const email = contactEmail(person);
            const name = contactName(person);
            return (
              <li key={person.id}>
                <Button
                  variant="ghost"
                  onClick={() =>
                    onChange({
                      name,
                      contactId: person.id,
                      companyId: person.company_id,
                      companyName: person.company_name ?? "",
                      email,
                    })
                  }
                >
                  {name}
                  {email ? ` · ${email}` : ""}
                  {person.company_name ? ` · ${person.company_name}` : ""}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </Stack>
  );
}
